/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { DashboardRow } from '@g17eco/molecules/dashboard';
import { Button } from 'reactstrap';
import { DataPeriods } from '@utils/dataPeriods';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { DataQualityIssueType, StatusFilter } from '@api/data-quality';
import {
  FilterState,
  STATUS_OPTIONS,
  CHECK_TYPE_OPTIONS,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  PERIOD_OPTIONS,
} from './constants';

interface SelectOption {
  value: string;
  label: string;
  searchString: string;
}

interface DataQualityFiltersSectionProps {
  filters: FilterState;
  initiativeOptions: SelectOption[];
  isLoading: boolean;
  onFilterChange: (key: keyof FilterState, values: string[] | DataPeriods[] | DataQualityIssueType[] | StatusFilter[]) => void;
  onRunCheck: () => void;
}

export const DataQualityFiltersSection = ({
  filters,
  initiativeOptions,
  isLoading,
  onFilterChange,
  onRunCheck,
}: DataQualityFiltersSectionProps) => {
  return (
    <DashboardRow classes={{ children: 'flex-lg-column px-0' }}>
      {/* Row 1: Month, Year, Period */}
      <div className='d-flex gap-2 mb-2'>
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Month'
          className='w-100'
          onChange={(values) => onFilterChange('months', values)}
          options={MONTH_OPTIONS}
          values={filters.months}
          isMenuPortalTargetBody
        />
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Year'
          className='w-100'
          onChange={(values) => onFilterChange('years', values)}
          options={YEAR_OPTIONS}
          values={filters.years}
          isMenuPortalTargetBody
        />
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Period'
          className='w-100'
          onChange={(values) => onFilterChange('periods', values as DataPeriods[])}
          options={PERIOD_OPTIONS}
          values={filters.periods}
          isMenuPortalTargetBody
        />
      </div>

      {/* Row 2: Status, Subsidiaries, Check Types */}
      <div className='d-flex gap-2 mb-2'>
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Data Status'
          className='w-100'
          onChange={(values) => onFilterChange('statuses', values as StatusFilter[])}
          options={STATUS_OPTIONS}
          values={filters.statuses}
          isMenuPortalTargetBody
        />
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Subsidiaries'
          className='w-100'
          onChange={(values) => onFilterChange('initiativeIds', values)}
          options={initiativeOptions}
          values={filters.initiativeIds}
          isMenuPortalTargetBody
          isFullWidthOptions
        />
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          placeholder='Check Types'
          className='w-100'
          onChange={(values) => onFilterChange('checkTypes', values as DataQualityIssueType[])}
          options={CHECK_TYPE_OPTIONS}
          values={filters.checkTypes}
          isMenuPortalTargetBody
        />
      </div>

      {/* Run Checks button */}
      <div className='d-flex justify-content-end'>
        <Button
          color='primary'
          onClick={onRunCheck}
          disabled={isLoading || filters.checkTypes.length === 0}
        >
          <i className='fas fa-play me-2' />
          Run Checks
        </Button>
      </div>
    </DashboardRow>
  );
};
